import React, {
  useEffect, useRef, useState, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import {
  motion, useInView, useMotionValue, useSpring, AnimatePresence,
} from 'framer-motion';
import {
  FiMapPin, FiMail, FiPhone, FiDownload, FiExternalLink,
  FiCalendar, FiBriefcase, FiBook, FiArrowRight,
} from 'react-icons/fi';
import { BsCheckCircleFill } from 'react-icons/bs';
import {
  setFrontendSkills, setBackendSkills, setToolSkills,
  setCommunicationSkills, setSoftSkills,
} from '../../redux/actions/actionCreate';
import {
  frontendSkillsData, backendSkillsData, toolsSkillsData,
  communicationSkillsData, softSkillsData,
} from './SkillsData';
import Imageside from './imageside';
import { images } from '../../constants';

// ─────────────────────────────────────────────────────────────────────────────
// STATIC DATA
// ─────────────────────────────────────────────────────────────────────────────
const ADVANCED_SKILLS = [
  {
    category: 'Backend & APIs', accent: '#f43f5e', num: '01', items: ['Node.js & TypeScript', 'Express, GraphQL, REST', 'Python & Django', 'JWT, OAuth2, RBAC'],
  },
  {
    category: 'Architecture & Patterns', accent: '#fb923c', num: '02', items: ['Microservices & Event-Driven', 'Kafka, Pub/Sub, Saga Patterns', 'Multi-Tenancy & Idempotent Workflows', 'Scalable System Design'],
  },
  {
    category: 'Cloud & DevOps', accent: '#34d399', num: '03', items: ['AWS (Lambda, ECS, EC2, S3)', 'Docker & Kubernetes', 'CI/CD (GitHub Actions)', 'Redis Caching'],
  },
  {
    category: 'Data & Databases', accent: '#60a5fa', num: '04', items: ['PostgreSQL, MongoDB, MySQL', 'Query Tuning, Indexing, Partitioning', 'Data Pipelines & ETL', 'Real-time Analytics'],
  },
  {
    category: 'Security & Integrations', accent: '#a78bfa', num: '05', items: ['OAuth2, SAML, JWT', 'Payments (M-Pesa, Multi-Currency)', 'Twilio/SMS, Webhooks', 'Government API Integrations'],
  },
  {
    category: 'AI & Observability', accent: '#fbbf24', num: '06', items: ['OpenAI API, LangChain', 'Vector Search', 'Structured Logging & Tracing', 'Performance Monitoring'],
  },
];

const EXPERIENCE = [
  {
    id: 'nd',
    role: 'Full-Stack Developer',
    company: 'Nathan Digital',
    type: 'HRMS & Payroll Implementation',
    period: 'Sep 2024 – Present',
    location: 'Nairobi, Onsite',
    current: true,
    accent: '#f43f5e',
    highlights: [
      'Architected responsive UI components for an enterprise HRMS platform serving 2,000+ employees across multiple regions.',
      'Designed secure REST APIs with JWT, OAuth2, and RBAC ensuring compliance with UAE payroll security standards.',
      'Built a real-time analytics dashboard using WebSockets, Chart.js, and REST APIs for live payroll & HR visibility.',
      'Developed a scalable shift management feature with automated allocation and bulk schedule updates.',
      'Integrated payroll features including tax deductions, benefits calculations, and UAE compliance reporting.',
    ],
    stack: ['React.js', 'Next.js', 'Node.js', 'Express', 'MongoDB', 'WebSockets', 'Chart.js', 'JWT', 'RBAC'],
  },
  {
    id: 'st',
    role: 'Full-Stack Developer',
    company: 'Symphony Technologies',
    type: 'Product Engineering',
    period: 'Feb 2022 – Mar 2024',
    location: 'Nairobi, Remote',
    current: false,
    accent: '#fb923c',
    highlights: [
      'Developed and maintained new user-facing features ensuring technical feasibility of UI/UX designs.',
      'Built efficient server-side APIs (RESTful & GraphQL), improving data handling speed by 30%.',
      'Integrated frontend with backend systems via REST APIs with optimized Redux state management.',
      'Designed and optimized databases improving query performance by 40%, supporting large data volumes.',
    ],
    stack: ['React.js', 'Node.js', 'GraphQL', 'PostgreSQL', 'Redux', 'Python'],
  },
];

const EDUCATION = [
  {
    id: 'mmu',
    institution: 'Multimedia University of Kenya',
    degree: "Bachelor's Degree",
    field: 'Mathematics & Computer Science',
    period: 'Sep 2012 – Dec 2016',
    accent: '#34d399',
    note: 'Exchange program with Google — built a Java security application that minimized laptop theft at the university.',
  },
];

const CERTIFICATIONS = [
  {
    id: 'mv',
    institution: 'Microverse',
    title: 'Remote Full Stack Web Development',
    period: 'May 2022 – Feb 2023',
    accent: '#f43f5e',
    note: '1300+ hours on algorithms, data structures, and full-stack development with Ruby, Rails, JavaScript, React & Redux.',
  },
  {
    id: 'goog',
    institution: 'Google',
    title: 'Web Development Program',
    period: 'May 2020 – Feb 2021',
    accent: '#60a5fa',
    note: 'Software architecture, system design, scalable web apps, Git workflows, and collaborative development.',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// UTILITIES
// ─────────────────────────────────────────────────────────────────────────────

// Converts hex to rgba for inline style use
const hexAlpha = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

// ── Animated stat counter ──────────────────────────────────────────────────
const Counter = ({ to, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const raw = useMotionValue(0);
  const spring = useSpring(raw, { stiffness: 55, damping: 20 });
  const [display, setDisplay] = useState(0);
  useEffect(() => { if (inView) raw.set(to); }, [inView, raw, to]);
  useEffect(() => { const u = spring.on('change', (v) => setDisplay(Math.floor(v))); return u; }, [spring]);
  return (
    <span ref={ref} className="tabular-nums">
      {display}
      {suffix}
    </span>
  );
};
Counter.propTypes = { to: PropTypes.number.isRequired, suffix: PropTypes.string };
Counter.defaultProps = { suffix: '' };

// ── Stat card ──────────────────────────────────────────────────────────────
const StatCard = ({ value, suffix, label }) => (
  <div className="group flex flex-col items-center gap-1.5 px-3 py-4 rounded-2xl border border-white/[0.08] bg-white/[0.03] hover:border-rose-500/30 hover:bg-rose-500/[0.04] transition-all duration-400 cursor-default">
    <span className="text-2xl font-black text-white leading-none tracking-tight">
      <Counter to={value} suffix={suffix} />
    </span>
    <span className="text-[9px] tracking-[0.2em] uppercase text-white/28 font-semibold text-center leading-tight">{label}</span>
  </div>
);
StatCard.propTypes = {
  value: PropTypes.number.isRequired, suffix: PropTypes.string, label: PropTypes.string.isRequired,
};
StatCard.defaultProps = { suffix: '' };

// ── Skill icon tile — generous sizing, real breathing room ────────────────
const SkillTile = ({ skill, index }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.85 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true, margin: '-20px' }}
    transition={{ delay: index * 0.04, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ y: -6, scale: 1.06 }}
    className="group flex flex-col items-center gap-3 cursor-default w-[72px]"
  >
    {/* Tile */}
    <div
      className="relative w-[56px] h-[56px] rounded-2xl flex items-center justify-center overflow-hidden transition-all duration-350"
      style={{
        // background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.09)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.07)',
      }}
    >
      {/* hover glow */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-350 rounded-2xl"
        style={{ background: 'radial-gradient(circle at 50% 50%, rgba(244,63,94,0.18) 0%, transparent 70%)' }}
      />
      {/* hover border */}
      <div
        className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-350"
        style={{ boxShadow: '0 0 0 1px rgba(244,63,94,0.45), 0 8px 24px rgba(244,63,94,0.15)' }}
      />
      <img
        src={skill.image}
        alt={skill.name}
        className="w-[52%] h-[52%] object-contain relative z-10 transition-transform duration-350 group-hover:scale-110"
      />
    </div>
    <span className="text-[10px] text-white/38 group-hover:text-white/70 transition-colors duration-300 text-center leading-tight font-medium tracking-wide w-full break-words">
      {skill.name}
    </span>
  </motion.div>
);
SkillTile.propTypes = {
  skill: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    image: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  index: PropTypes.number.isRequired,
};

// ── Icon skill group — wide spacing, section feel ─────────────────────────
const IconSkillGroup = ({ title, skills, accent }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-30px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 16 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="mb-10 last:mb-0"
    >
      {/* Label row */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: hexAlpha(accent, 0.15), border: `1px solid ${hexAlpha(accent, 0.4)}` }}>
          <div className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
        </div>
        <span className="text-[10px] font-black tracking-[0.3em] uppercase" style={{ color: hexAlpha(accent, 0.8) }}>{title}</span>
        <div className="flex-1 h-px" style={{ background: `linear-gradient(to right, ${hexAlpha(accent, 0.2)}, transparent)` }} />
        <span className="text-[9px] text-white/18 tabular-nums font-mono">{Array.isArray(skills) ? String(skills.length).padStart(2, '0') : '00'}</span>
      </div>
      {/* Tile row — flex-wrap with consistent gap */}
      <div className="flex flex-wrap gap-x-5 gap-y-6 pl-7">
        {Array.isArray(skills) && skills.map((skill, i) => (
          <SkillTile key={skill.id} skill={skill} index={i} />
        ))}
      </div>
    </motion.div>
  );
};
IconSkillGroup.propTypes = {
  title: PropTypes.string.isRequired,
  skills: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    image: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  })).isRequired,
  accent: PropTypes.string.isRequired,
};

// ── Advanced skill card — numbered, display-type heading ─────────────────
const AdvancedSkillCard = ({
  category, accent, num, items, index: catIdx,
}) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-30px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: catIdx * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="group relative overflow-hidden rounded-2xl p-5 transition-all duration-400 cursor-default"
      style={{
        background: 'rgba(255,255,255,0.025)',
        border: '1px solid rgba(255,255,255,0.07)',
      }}
    >
      {/* Hover accent fill */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{ background: `radial-gradient(ellipse at 0% 0%, ${hexAlpha(accent, 0.07)} 0%, transparent 65%)` }}
      />
      {/* Large background number — decorative */}
      <span
        className="absolute -right-2 -top-3 font-black leading-none select-none pointer-events-none"
        style={{ fontSize: '4.5rem', color: hexAlpha(accent, 0.07), fontFamily: 'var(--font-display)' }}
      >
        {num}
      </span>

      {/* Accent bar top */}
      <div className="h-px w-8 mb-4 rounded-full" style={{ background: accent }} />

      {/* Category heading */}
      <h3
        className="text-sm font-black tracking-wide leading-tight mb-4 uppercase relative z-10"
        style={{ color: accent }}
      >
        {category}
      </h3>

      {/* Items — each with a micro left-accent */}
      <div className="flex flex-col gap-0 relative z-10">
        {items.map((item, i) => (
          <div
            key={item}
            className="flex items-start gap-3 py-2.5 border-b last:border-b-0"
            style={{ borderColor: 'rgba(255,255,255,0.05)' }}
          >
            <span
              className="mt-1.5 w-1 h-1 rounded-full flex-shrink-0 opacity-70"
              style={{
                background: accent,
                // Stagger opacity slightly for visual rhythm
                opacity: 0.4 + (i * 0.15),
              }}
            />
            <span className="text-xs text-white/48 group-hover:text-white/65 transition-colors duration-300 leading-snug font-light">
              {item}
            </span>
          </div>
        ))}
      </div>

      {/* Bottom border accent on hover */}
      <div
        className="absolute bottom-0 left-0 right-0 h-px opacity-0 group-hover:opacity-100 transition-opacity duration-400"
        style={{ background: `linear-gradient(to right, ${accent}, transparent)` }}
      />
    </motion.div>
  );
};
AdvancedSkillCard.propTypes = {
  category: PropTypes.string.isRequired,
  accent: PropTypes.string.isRequired,
  num: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
  index: PropTypes.number.isRequired,
};

// ── Experience card ────────────────────────────────────────────────────────
const ExperienceCard = ({ job, index: jobIdx }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-30px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: 20 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ delay: jobIdx * 0.1, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="relative flex gap-5 pb-10 last:pb-0"
    >
      {/* Timeline */}
      <div className="flex flex-col items-center flex-shrink-0 pt-1">
        <div
          className="w-3 h-3 rounded-full border-2 flex-shrink-0 z-10"
          style={{
            borderColor: job.accent,
            background: job.current ? job.accent : 'transparent',
            boxShadow: job.current ? `0 0 0 4px ${hexAlpha(job.accent, 0.15)}, 0 0 14px ${hexAlpha(job.accent, 0.4)}` : 'none',
          }}
        />
        <div className="w-px flex-1 mt-2" style={{ background: 'linear-gradient(to bottom, rgba(255,255,255,0.1), transparent)' }} />
      </div>

      {/* Card */}
      <div
        className="flex-1 min-w-0 rounded-2xl p-5 mb-2 group hover:border-white/12 transition-all duration-400"
        style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}
      >
        {/* Top row */}
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2.5 flex-wrap mb-1">
              <h3 className="text-sm font-bold text-white tracking-wide">{job.role}</h3>
              {job.current && (
                <span
                  className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[9px] font-black tracking-[0.15em] uppercase"
                  style={{ background: hexAlpha(job.accent, 0.12), color: job.accent, border: `1px solid ${hexAlpha(job.accent, 0.25)}` }}
                >
                  <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: job.accent }} />
                  Current
                </span>
              )}
            </div>
            <p className="text-[13px] font-bold" style={{ color: job.accent }}>{job.company}</p>
            <p className="text-[10px] text-white/30 mt-0.5 font-medium">{job.type}</p>
          </div>
          <div className="flex flex-col items-end gap-1.5 flex-shrink-0 text-right">
            <div className="flex items-center gap-1.5 text-[10px] text-white/30">
              <FiCalendar className="w-3 h-3 flex-shrink-0" />
              <span className="font-medium">{job.period}</span>
            </div>
            <div className="flex items-center gap-1.5 text-[10px] text-white/22">
              <FiMapPin className="w-3 h-3 flex-shrink-0" />
              {job.location}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px mb-4" style={{ background: 'rgba(255,255,255,0.06)' }} />

        {/* Highlights */}
        <ul className="flex flex-col gap-2.5 mb-5">
          {job.highlights.map((h) => (
            <li key={h} className="flex items-start gap-2.5">
              <BsCheckCircleFill
                className="w-3 h-3 flex-shrink-0 mt-0.5"
                style={{ color: job.accent, opacity: 0.55 }}
              />
              <span className="text-[11.5px] text-white/40 leading-relaxed font-light">{h}</span>
            </li>
          ))}
        </ul>

        {/* Stack tags */}
        <div className="flex flex-wrap gap-1.5 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          {job.stack.map((tech) => (
            <span
              key={tech}
              className="px-2.5 py-0.5 rounded-full text-[9px] font-bold tracking-wide uppercase"
              style={{
                color: job.accent,
                background: hexAlpha(job.accent, 0.08),
                border: `1px solid ${hexAlpha(job.accent, 0.2)}`,
              }}
            >
              {tech}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  );
};
ExperienceCard.propTypes = {
  job: PropTypes.shape({
    id: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
    company: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    period: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
    current: PropTypes.bool.isRequired,
    accent: PropTypes.string.isRequired,
    highlights: PropTypes.arrayOf(PropTypes.string).isRequired,
    stack: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  index: PropTypes.number.isRequired,
};

// ── Education / cert card ──────────────────────────────────────────────────
const EducationCard = ({ item, isCert, index: itemIdx }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-30px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 14 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: itemIdx * 0.09, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className="group relative overflow-hidden rounded-2xl p-5 transition-all duration-400"
      style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)' }}
    >
      {/* Hover glow */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{ background: `radial-gradient(ellipse at 100% 100%, ${hexAlpha(item.accent, 0.06)} 0%, transparent 65%)` }}
      />
      <div className="flex items-start gap-4 relative z-10">
        {/* Icon */}
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: hexAlpha(item.accent, 0.1), border: `1px solid ${hexAlpha(item.accent, 0.25)}` }}
        >
          {isCert
            ? <FiBriefcase className="w-4 h-4" style={{ color: item.accent }} />
            : <FiBook className="w-4 h-4" style={{ color: item.accent }} />}
        </div>
        {/* Content */}
        <div className="flex-1 min-w-0">
          <span
            className="text-[9px] font-black tracking-[0.22em] uppercase block mb-1"
            style={{ color: hexAlpha(item.accent, 0.85) }}
          >
            {item.institution}
          </span>
          <h3 className="text-[13px] font-bold text-white leading-tight mb-1.5">
            {isCert ? item.title : item.degree}
          </h3>
          {!isCert && item.field && (
            <p className="text-[11px] text-white/35 font-medium mb-1">{item.field}</p>
          )}
          <div className="flex items-center gap-1.5 mb-3">
            <FiCalendar className="w-2.5 h-2.5 text-white/20" />
            <span className="text-[10px] text-white/25 font-medium">{item.period}</span>
          </div>
          {item.note && (
            <p
              className="text-[11px] text-white/30 leading-relaxed pt-3"
              style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
            >
              {item.note}
            </p>
          )}
        </div>
      </div>
      {/* Left accent bar */}
      <div
        className="absolute left-0 top-4 bottom-4 w-px opacity-40 group-hover:opacity-80 transition-opacity duration-400"
        style={{ background: item.accent }}
      />
    </motion.div>
  );
};
EducationCard.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    institution: PropTypes.string.isRequired,
    period: PropTypes.string.isRequired,
    accent: PropTypes.string.isRequired,
    note: PropTypes.string,
    degree: PropTypes.string,
    field: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
  isCert: PropTypes.bool,
  index: PropTypes.number.isRequired,
};
EducationCard.defaultProps = { isCert: false };

// ── Tab button ────────────────────────────────────────────────────────────
const TabBtn = ({
  label, icon, active, onClick, count,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="relative flex items-center gap-2 px-5 py-2.5 rounded-full text-[10px] font-black tracking-[0.2em] uppercase transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400"
    style={active ? {
      background: 'linear-gradient(135deg, #f43f5e, #e11d48)',
      color: '#fff',
      boxShadow: '0 4px 20px rgba(244,63,94,0.3), 0 0 0 1px rgba(244,63,94,0.4)',
    } : {
      background: 'rgba(255,255,255,0.04)',
      color: 'rgba(255,255,255,0.4)',
      border: '1px solid rgba(255,255,255,0.08)',
    }}
  >
    {icon}
    {label}
    {count !== undefined && (
      <span
        className="text-[8px] px-1.5 py-0.5 rounded-full font-bold"
        style={active ? { background: 'rgba(255,255,255,0.2)', color: '#fff' } : { background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.3)' }}
      >
        {count}
      </span>
    )}
  </button>
);
TabBtn.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  active: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  count: PropTypes.number,
};
TabBtn.defaultProps = { count: undefined };

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const About = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('skills');

  const frontendSkills = useSelector((s) => s.frontendskills);
  const backendSkills = useSelector((s) => s.backendskills);
  const toolSkills = useSelector((s) => s.toolskills);
  const communicationSkills = useSelector((s) => s.communicationskills);

  useEffect(() => {
    dispatch(setFrontendSkills(frontendSkillsData));
    dispatch(setBackendSkills(backendSkillsData));
    dispatch(setToolSkills(toolsSkillsData));
    dispatch(setCommunicationSkills(communicationSkillsData));
    dispatch(setSoftSkills(softSkillsData));
  }, [dispatch]);

  const iconGroups = [
    { title: 'Front-End', skills: frontendSkills, accent: '#f43f5e' },
    { title: 'Back-End', skills: backendSkills, accent: '#fb923c' },
    { title: 'Tools & DevOps', skills: toolSkills, accent: '#34d399' },
    { title: 'Communication', skills: communicationSkills, accent: '#a78bfa' },
  ];

  const handleTabChange = useCallback((id) => setActiveTab(id), []);

  const totalSkills = [frontendSkills, backendSkills, toolSkills, communicationSkills]
    .reduce((acc, arr) => acc + (Array.isArray(arr) ? arr.length : 0), 0);

  return (
    <div
      className="relative w-full h-screen overflow-y-auto text-white"
      style={{
        backgroundImage: `url(${images.bg3})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* ── Multi-layer atmospheric overlay — lets bg breathe ── */}
      {/* <div
        className="absolute inset-0 z-0 pointer-events-none"
        style={{
          background: 'linear-gradient(135deg,
          rgba(6,4,2,0.91) 0%, rgba(8,5,3,0.82) 40%, rgba(10,6,4,0.88) 100%)',
        }}
      /> */}
      {/* Vignette edges */}
      {/* <div
        className="absolute inset-0 z-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 50% 50%, transparent 40%, rgba(0,0,0,0.55) 100%)',
        }}
      /> */}

      {/* ── Decorative rose orb — top right ── */}
      <div
        className="pointer-events-none absolute top-0 right-0 w-[50vw] h-[50vw] z-0"
        style={{
          background: 'radial-gradient(circle at 80% 20%, rgba(244,63,94,0.09) 0%, transparent 55%)',
        }}
      />
      {/* Orange orb — bottom left */}
      <div
        className="pointer-events-none absolute bottom-0 left-0 w-[40vw] h-[40vw] z-0"
        style={{
          background: 'radial-gradient(circle at 20% 80%, rgba(251,146,60,0.06) 0%, transparent 55%)',
        }}
      />

      {/* ── MUSOKO watermark ── */}
      <div className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center overflow-hidden">
        <span
          className="select-none font-black leading-none tracking-tighter"
          style={{
            fontSize: 'clamp(10rem, 28vw, 22rem)',
            fontFamily: 'var(--font-display)',
            color: 'rgba(255,255,255,0.012)',
            transform: 'rotate(-15deg) translateY(8%)',
            whiteSpace: 'nowrap',
          }}
        >
          MUSOKO
        </span>
      </div>

      {/* ══════════════════════════════════════════════════════════════
          MAIN LAYOUT
      ══════════════════════════════════════════════════════════════ */}
      <div className="relative z-10 flex flex-col lg:flex-row w-full min-h-full">

        {/* ════════════════════ LEFT PANEL ════════════════════ */}
        <motion.div
          className="relative flex flex-col w-full lg:w-[34%] xl:w-[30%] px-7 sm:px-10 pt-24 pb-8
                     lg:sticky lg:top-0 lg:h-screen lg:overflow-y-auto
                     border-b lg:border-b-0 lg:border-r"
          style={{ borderColor: 'rgba(255,255,255,0.07)' }}
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Spine text */}
          <div className="hidden lg:flex absolute right-0 top-0 bottom-0 w-7 items-center justify-center z-10">
            <span
              className="text-[8px] font-black uppercase select-none"
              style={{
                writingMode: 'vertical-rl', transform: 'rotate(180deg)', color: 'rgba(255,255,255,0.08)', letterSpacing: '0.35em',
              }}
            >
              Full-Stack Engineer · Nairobi
            </span>
          </div>

          {/* ── Section label ── */}
          <p className="text-[9px] font-black tracking-[0.45em] uppercase mb-4" style={{ color: 'rgba(255,255,255,0.2)' }}>
            — About me
          </p>

          {/* ── Heading ── */}
          <h2 className="font-display text-[2.4rem] sm:text-[2.75rem] tracking-tight leading-[1.05] mb-6">
            About
            {' '}
            <span className="italic text-rose-400">me</span>
          </h2>

          {/* ── Profile image ── */}
          <div className="mb-5">
            <Imageside />
          </div>

          {/* ── Availability badge ── */}
          <div
            className="self-start flex items-center gap-2 px-3.5 py-1.5 rounded-full mb-5"
            style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.22)' }}
          >
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[10px] font-black tracking-[0.18em] uppercase text-emerald-400">Available — Remote</span>
          </div>

          {/* ── Bio ── */}
          <p className="text-[11.5px] leading-[1.9] mb-5 font-light" style={{ color: 'rgba(255,255,255,0.44)' }}>
            Full Stack Software Engineer with
            {' '}
            <span className="font-semibold" style={{ color: 'rgba(255,255,255,0.72)' }}>4+ years of experience</span>
            {' '}
            building scalable web applications using JavaScript,
            TypeScript, React, Node.js, and PostgreSQL.
            Currently working on an enterprise
            {' '}
            <span className="font-medium" style={{ color: 'rgba(244,63,94,0.75)' }}>HRMS & payroll platform</span>
            {' '}
            serving 2,000+ employees across multiple jurisdictions.
          </p>

          {/* ── Contact info ── */}
          <div className="flex flex-col gap-3 mb-6">
            {[
              { icon: FiMapPin, text: 'Nairobi, Kenya · Open to Remote' },
              { icon: FiMail, text: 'mussocollins@gmail.com', href: 'mailto:mussocollins@gmail.com' },
              { icon: FiPhone, text: '+254 717 684 174', href: 'tel:+254717684174' },
            ].map(({ icon: Icon, text, href }) => {
              const inner = (
                <>
                  <Icon className="w-3 h-3 flex-shrink-0" style={{ color: 'rgba(244,63,94,0.6)' }} />
                  <span>{text}</span>
                </>
              );
              return href ? (
                <a
                  key={text}
                  href={href}
                  className="flex items-center gap-2.5 text-[11px] font-medium transition-colors duration-200"
                  style={{ color: 'rgba(255,255,255,0.35)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.35)'; }}
                >
                  {inner}
                </a>
              ) : (
                <div key={text} className="flex items-center gap-2.5 text-[11px] font-medium" style={{ color: 'rgba(255,255,255,0.35)' }}>
                  {inner}
                </div>
              );
            })}
          </div>

          {/* ── Resume buttons ── */}
          <div className="flex flex-col sm:flex-row gap-2.5 mb-7">
            <a
              href="https://drive.google.com/file/d/1mHkAQJHt2OP0qD8d50Hdhox9Am6KTv3_/view?usp=sharing"
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-white text-[11px] font-black tracking-[0.15em] uppercase transition-all duration-300 group"
              style={{
                background: 'linear-gradient(135deg, #f43f5e, #e11d48)',
                boxShadow: '0 4px 16px rgba(244,63,94,0.22)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 6px 22px rgba(244,63,94,0.35)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(244,63,94,0.22)'; }}
            >
              <FiExternalLink className="w-3.5 h-3.5" />
              View Resume
            </a>
            <a
              href="https://drive.google.com/file/d/1mHkAQJHt2OP0qD8d50Hdhox9Am6KTv3_/view?usp=sharing"
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-[11px] font-black tracking-[0.15em] uppercase transition-all duration-300"
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.5)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.09)'; e.currentTarget.style.color = 'rgba(255,255,255,0.8)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
            >
              <FiDownload className="w-3.5 h-3.5" />
              Download CV
            </a>
          </div>

          {/* ── Stat counters ── */}
          <div className="grid grid-cols-3 gap-2 mt-auto">
            <StatCard value={4} suffix="+" label="Years exp." />
            <StatCard value={30} suffix="+" label="Projects" />
            <StatCard value={15} suffix="+" label="Clients" />
          </div>
        </motion.div>

        {/* ════════════════════ RIGHT PANEL ════════════════════ */}
        <motion.div
          className="flex-1 flex flex-col px-7 sm:px-10 pt-12 lg:pt-24 pb-12 min-w-0"
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        >

          {/* ── Tab navigation ── */}
          <div className="flex items-center gap-2 flex-wrap mb-10">
            <TabBtn
              label="Skills"
              icon={<span style={{ fontSize: 11 }}>⚡</span>}
              active={activeTab === 'skills'}
              onClick={() => handleTabChange('skills')}
              count={totalSkills > 0 ? totalSkills : undefined}
            />
            <TabBtn
              label="Experience"
              icon={<FiBriefcase className="w-3 h-3" />}
              active={activeTab === 'experience'}
              onClick={() => handleTabChange('experience')}
              count={EXPERIENCE.length}
            />
            <TabBtn
              label="Education"
              icon={<FiBook className="w-3 h-3" />}
              active={activeTab === 'education'}
              onClick={() => handleTabChange('education')}
              count={EDUCATION.length + CERTIFICATIONS.length}
            />
          </div>

          {/* ── Tab content ── */}
          <AnimatePresence mode="wait">

            {/* ══ SKILLS ══ */}
            {activeTab === 'skills' && (
              <motion.div
                key="skills"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35 }}
              >
                {/* Section title */}
                <div className="mb-10">
                  <p className="text-[9px] font-black tracking-[0.4em] uppercase mb-2" style={{ color: 'rgba(255,255,255,0.18)' }}>— Tech arsenal</p>
                  <h2 className="font-display text-3xl sm:text-[2.25rem] tracking-tight text-white leading-none">
                    Skills &amp;
                    {' '}
                    <span className="italic" style={{ color: '#fb923c' }}>tools</span>
                  </h2>
                  <div className="mt-3 h-px" style={{ background: 'linear-gradient(to right, rgba(244,63,94,0.4), rgba(251,146,60,0.15), transparent)' }} />
                </div>

                {/* ── Core tech icon grid ── */}
                <div className="mb-12">
                  <div className="flex items-center gap-3 mb-7">
                    <span className="text-[9px] font-black tracking-[0.3em] uppercase" style={{ color: 'rgba(255,255,255,0.2)' }}>Core technologies</span>
                    <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.06)' }} />
                  </div>
                  {iconGroups.map(({ title, skills, accent }) => (
                    <IconSkillGroup key={title} title={title} skills={skills} accent={accent} />
                  ))}
                </div>

                {/* ── Advanced capabilities — numbered cards ── */}
                <div>
                  <div className="flex items-center gap-3 mb-7">
                    <span className="text-[9px] font-black tracking-[0.3em] uppercase" style={{ color: 'rgba(255,255,255,0.2)' }}>Advanced capabilities</span>
                    <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.06)' }} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                    {ADVANCED_SKILLS.map((cat, i) => (
                      <AdvancedSkillCard
                        key={cat.category}
                        category={cat.category}
                        accent={cat.accent}
                        num={cat.num}
                        items={cat.items}
                        index={i}
                      />
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* ══ EXPERIENCE ══ */}
            {activeTab === 'experience' && (
              <motion.div
                key="experience"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35 }}
              >
                <div className="mb-10">
                  <p className="text-[9px] font-black tracking-[0.4em] uppercase mb-2" style={{ color: 'rgba(255,255,255,0.18)' }}>— Career</p>
                  <h2 className="font-display text-3xl sm:text-[2.25rem] tracking-tight leading-none">
                    <span className="text-white">Professional</span>
                    {' '}
                    <span className="italic" style={{ color: '#f43f5e' }}>experience</span>
                  </h2>
                  <div className="mt-3 h-px" style={{ background: 'linear-gradient(to right, rgba(244,63,94,0.4), transparent)' }} />
                </div>
                <div className="flex flex-col">
                  {EXPERIENCE.map((job, i) => (
                    <ExperienceCard key={job.id} job={job} index={i} />
                  ))}
                </div>
              </motion.div>
            )}

            {/* ══ EDUCATION ══ */}
            {activeTab === 'education' && (
              <motion.div
                key="education"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35 }}
              >
                <div className="mb-10">
                  <p className="text-[9px] font-black tracking-[0.4em] uppercase mb-2" style={{ color: 'rgba(255,255,255,0.18)' }}>— Background</p>
                  <h2 className="font-display text-3xl sm:text-[2.25rem] tracking-tight leading-none">
                    <span className="text-white">Education &amp;</span>
                    {' '}
                    <span className="italic" style={{ color: '#34d399' }}>certifications</span>
                  </h2>
                  <div className="mt-3 h-px" style={{ background: 'linear-gradient(to right, rgba(52,211,153,0.4), transparent)' }} />
                </div>

                <div className="flex flex-col gap-4">
                  <p className="text-[9px] font-black tracking-[0.3em] uppercase" style={{ color: 'rgba(255,255,255,0.18)' }}>— Academic degree</p>
                  {EDUCATION.map((ed, i) => (
                    <EducationCard key={ed.id} item={ed} isCert={false} index={i} />
                  ))}

                  <p className="text-[9px] font-black tracking-[0.3em] uppercase mt-4" style={{ color: 'rgba(255,255,255,0.18)' }}>— Certifications & programs</p>
                  {CERTIFICATIONS.map((cert, i) => (
                    <EducationCard key={cert.id} item={cert} isCert index={i} />
                  ))}
                </div>

                {/* CTA */}
                <motion.div
                  className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 mt-10 pt-6"
                  style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                >
                  <p className="text-xs leading-relaxed max-w-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>
                    Always learning. Currently open to
                    {' '}
                    <span style={{ color: '#f43f5e', fontWeight: 600 }}>full-time roles</span>
                    {' '}
                    and
                    {' '}
                    <span style={{ color: '#fb923c', fontWeight: 600 }}>freelance projects</span>
                  </p>
                  <a
                    href="#contact"
                    className="flex-shrink-0 flex items-center gap-2 px-6 py-3 rounded-full text-white text-[11px] font-black tracking-[0.18em] uppercase transition-all duration-300 group"
                    style={{
                      background: 'linear-gradient(135deg, #f43f5e, #e11d48)',
                      boxShadow: '0 4px 16px rgba(244,63,94,0.22)',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 6px 24px rgba(244,63,94,0.38)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(244,63,94,0.22)'; }}
                  >
                    Hire me
                    <FiArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-300" />
                  </a>
                </motion.div>
              </motion.div>
            )}

          </AnimatePresence>
        </motion.div>

      </div>
    </div>
  );
};

export default About;
